/**
 * LocalStorage Cache Utility
 * Provides a simple caching mechanism using localStorage with TTL support
 */

interface CacheItem<T> {
  data: T
  timestamp: number
  ttl: number // Time to live in milliseconds
}

/**
 * Get cached data from localStorage
 * @param key - Cache key
 * @returns Cached data if valid, null if expired or not found
 */
export function getCachedData<T>(key: string): T | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }

    const cached = localStorage.getItem(key)
    if (!cached) {
      return null
    }

    const item: CacheItem<T> = JSON.parse(cached)
    const now = Date.now()

    // Check if cache is expired
    if (now - item.timestamp > item.ttl) {
      // Remove expired cache
      localStorage.removeItem(key)
      return null
    }

    return item.data
  } catch (error) {
    // Clear invalid cache
    try {
      localStorage.removeItem(key)
    } catch {
      // Ignore errors when clearing
    }
    return null
  }
}

/**
 * Set cached data in localStorage
 * @param key - Cache key
 * @param data - Data to cache
 * @param ttl - Time to live in milliseconds (default: 5 minutes)
 */
export function setCachedData<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
  try {
    if (typeof window === 'undefined') {
      return
    }

    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl,
    }

    localStorage.setItem(key, JSON.stringify(item))
  } catch (error) {
    // If storage is full, try to clear some old caches
    try {
      clearExpiredCaches()
      // Retry once
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now(), ttl }))
    } catch (retryError) {
    }
  }
}

/**
 * Clear cached data from localStorage
 * @param key - Cache key (optional, clears all caches if not provided)
 */
export function clearCachedData(key?: string): void {
  try {
    if (typeof window === 'undefined') {
      return
    }

    if (key) {
      localStorage.removeItem(key)
    } else {
      // Clear all cache keys (keys starting with 'cache_')
      const keys = Object.keys(localStorage)
      keys.forEach((k) => {
        if (k.startsWith('cache_')) {
          localStorage.removeItem(k)
        }
      })
    }
  } catch (error) {
  }
}

/**
 * Clear all expired caches
 */
function clearExpiredCaches(): void {
  try {
    if (typeof window === 'undefined') {
      return
    }

    const keys = Object.keys(localStorage)
    const now = Date.now()

    keys.forEach((key) => {
      if (key.startsWith('cache_')) {
        try {
          const cached = localStorage.getItem(key)
          if (cached) {
            const item: CacheItem<any> = JSON.parse(cached)
            if (now - item.timestamp > item.ttl) {
              localStorage.removeItem(key)
            }
          }
        } catch {
          // Remove invalid cache entries
          localStorage.removeItem(key)
        }
      }
    })
  } catch (error) {
  }
}

/**
 * Check if cached data exists and is valid
 * @param key - Cache key
 * @returns true if cache exists and is valid, false otherwise
 */
export function hasValidCache(key: string): boolean {
  return getCachedData(key) !== null
}

/**
 * Get cache key with prefix
 * @param key - Base key
 * @returns Prefixed cache key
 */
export function getCacheKey(key: string): string {
  return `cache_${key}`
}

